/**
 * Homepage coverage selector model (V26, 20 Sep 2026).
 *
 * The handoff asks for `packageType -> coverageHours -> availableVariations`, every slider position a real package.
 * The slider walks the twelve coverage hours, one position per hour, so the labels are evenly spaced. Within an hour,
 * Photo and Photo + Video sell more photos in steps of ten at $50 a step (Sales Playbook, Traci, 16 Jul 2026, wired
 * 21 Sep 2026); that is a second control beside the slider, not extra slider positions, so the hour labels stay
 * legible. Every number comes from packagePricing.js.
 */
import { ladderFor, normalizePackageType, MAX_PACKAGE_HOURS, basePhotosFor, maxPhotosFor, photoOptionsFor as photoOptionsForType, packagePriceFor, PHOTO_STEP, PHOTO_STEP_PRICE } from '../constants/packagePricing.js'

export { PHOTO_STEP, PHOTO_STEP_PRICE }

export const SERVICES = [
  { key: 'photo', label: 'Photo', ladderType: 'photo', coverage: 'Photo coverage', bookingLabel: 'Photo only' },
  { key: 'video', label: 'Video', ladderType: 'videoOnly', coverage: 'Video coverage', bookingLabel: 'Video only' },
  { key: 'combo', label: 'Photo + Video', ladderType: 'photoVideo', coverage: 'Photo + Video coverage', bookingLabel: 'Photo + Video' }
]

export const PRESET_HOURS = [1, 8, 12]
export const DEFAULT_HOURS = 8

const PRESET_TAGS = { 1: 'Intimate', 8: 'Most Popular', 12: 'Most Customizable' }

export function serviceByKey(key) {
  return SERVICES.find(s => s.key === key) || SERVICES[0]
}

/** Every production variation for a service: one per coverage hour, straight from the ladder. */
export function variationsFor(serviceKey) {
  const service = serviceByKey(serviceKey)
  const ladder = ladderFor(service.ladderType) || []
  return ladder
    .filter(p => Number.isFinite(p?.hours) && Number.isFinite(p?.price) && p.hours >= 1 && p.hours <= MAX_PACKAGE_HOURS)
    .map(p => ({
      id: `${service.ladderType}-${p.hours}h`,
      service: service.key,
      packageType: service.ladderType,
      hours: p.hours,
      price: p.price,
      photos: maxPhotosFor(service.ladderType, p.hours) === null ? null : basePhotosFor(p.hours),
      basePhotos: maxPhotosFor(service.ladderType, p.hours) === null ? null : basePhotosFor(p.hours),
      maxPhotos: maxPhotosFor(service.ladderType, p.hours)
    }))
    .sort((a, b) => a.hours - b.hours)
}

/** The photo counts sold at this size for this service, base first, each with its price. Video: one, no count. */
export function photoOptionsFor(serviceKey, hours) {
  const service = serviceByKey(serviceKey)
  return photoOptionsForType(service.ladderType, hours)
}

/** The variation for a size, optionally with a photo count above the base (priced by the playbook rule). */
export function variationFor(serviceKey, hours, photos = null) {
  const base = variationsFor(serviceKey).find(v => v.hours === Number(hours)) || null
  if (!base || photos === null || photos === undefined || base.photos === null) return base
  const price = packagePriceFor(base.packageType, base.hours, photos)
  if (price === null) return base
  return { ...base, id: `${base.id}-${photos}p`, photos: Number(photos), price }
}

/** The three preset cards for a service, with the tag copy the design uses. */
export function presetOptions(serviceKey) {
  return PRESET_HOURS.map(h => {
    const v = variationFor(serviceKey, h)
    return v ? { ...v, tag: PRESET_TAGS[h] || '' } : null
  }).filter(Boolean)
}

/**
 * Where each hour label sits on the slider, as a percentage of the track. The slider's positions are the
 * variations in order, so the label for hour h goes at the first index whose variation is h hours.
 */
export function sliderLabels(serviceKey) {
  const list = variationsFor(serviceKey)
  const maxIndex = Math.max(1, list.length - 1)
  const out = []
  for (let h = 1; h <= MAX_PACKAGE_HOURS; h++) {
    const idx = list.findIndex(v => v.hours === h)
    if (idx < 0) continue
    out.push({ hours: h, index: idx, left: (idx / maxIndex) * 100 })
  }
  return out
}

export function formatDollars(n) {
  return '$' + Math.round(Number(n) || 0).toLocaleString('en-US')
}

export function describeSelection(variation) {
  if (!variation) return ''
  const service = serviceByKey(variation.service)
  const photos = variation.photos ? ` • ${variation.photos} photos` : ''
  return `${variation.hours} hr${variation.hours === 1 ? '' : 's'}${photos} • ${service.coverage}`
}

/**
 * The booking handoff. Express checkout's plain form (`package_type` = internal key, `hours` = 1..12) is the
 * canonical selection; the server prices it from the ladder, so no price travels in the URL and nothing the
 * browser could edit reaches the contract.
 */
export function bookingUrlFor(variation) {
  if (!variation) return '/book'
  const params = new URLSearchParams({ package_type: variation.packageType, hours: String(variation.hours) })
  // a photo count above the base travels too; the server re-prices it from the same table
  if (variation.photos && variation.basePhotos && variation.photos > variation.basePhotos) params.set('photos', String(variation.photos))
  return `/book?${params.toString()}`
}

/** Guard for anything arriving from a URL or storage: only a real production variation comes back. */
export function resolveVariation({ service, packageType, hours, photos = null } = {}) {
  const key = service && SERVICES.some(s => s.key === service)
    ? service
    : SERVICES.find(s => s.ladderType === normalizePackageType(packageType))?.key
  if (!key) return null
  return variationFor(key, hours, photos)
}
